import { useState, useEffect, useCallback, useRef } from 'react';

import { getStakeSessionStatus } from '../../../../api/paymentService';
import type { StakePaymentSession, StakeSessionStatus } from '../../../../types/payment';

const STAKE_POLLING_INTERVAL_MS = 5000;
const STAKE_POLLING_MAX_ATTEMPTS = 120; // 120 * 5s = 10min max

const TERMINAL_STAKE_STATUSES: StakeSessionStatus[] = [
  'CONFIRMED_EXACT',
  'OVERPAID',
  'TIMEOUT',
  'REFUND_INITIATED',
  'REFUNDED',
  'REFUND_FAILED',
];

const isTerminalStakeStatus = (status: StakeSessionStatus): boolean =>
  TERMINAL_STAKE_STATUSES.includes(status);

export interface UseStakeSessionPollingOptions {
  sessionId: number | null;
  onSessionUpdate: (session: StakePaymentSession) => void;
}

export interface UseStakeSessionPollingResult {
  isPolling: boolean;
  startPolling: () => void;
  /** Stop polling. Pass `{ terminal: true }` to permanently block restart until sessionId changes. */
  stopPolling: (opts?: { terminal?: boolean }) => void;
  fetchNow: () => Promise<void>;
}

/**
 * Polls GET /payment-sessions/:id for stake session status updates.
 * Fallback mechanism when socket paymentSessionUpdated doesn't arrive.
 *
 * - Polls every 5s while active
 * - Stops on terminal status (CONFIRMED_EXACT, OVERPAID, TIMEOUT, REFUND_*)
 * - Stops after max attempts (120 = 10min)
 * - Pauses when tab is hidden, resumes on visible
 * - Guards against concurrent in-flight requests
 */
export function useStakeSessionPolling(
  options: UseStakeSessionPollingOptions,
): UseStakeSessionPollingResult {
  const { sessionId, onSessionUpdate } = options;

  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const stoppedByTerminalRef = useRef(false);
  const inFlightRef = useRef(false);
  const attemptCountRef = useRef(0);
  const onSessionUpdateRef = useRef(onSessionUpdate);
  onSessionUpdateRef.current = onSessionUpdate;

  const clearPollingInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchOnce = useCallback(async () => {
    if (!sessionId || !mountedRef.current || inFlightRef.current) return;

    inFlightRef.current = true;
    try {
      const session = await getStakeSessionStatus(sessionId);
      if (!mountedRef.current) return;

      attemptCountRef.current += 1;
      onSessionUpdateRef.current(session);

      if (isTerminalStakeStatus(session.status)) {
        stoppedByTerminalRef.current = true;
        clearPollingInterval();
        setIsPolling(false);
      } else if (attemptCountRef.current >= STAKE_POLLING_MAX_ATTEMPTS) {
        clearPollingInterval();
        setIsPolling(false);
      }
    } catch (err) {
      // Swallow polling errors — socket is primary, this is fallback
      console.warn('Stake session poll failed:', err);
      attemptCountRef.current += 1;
      if (attemptCountRef.current >= STAKE_POLLING_MAX_ATTEMPTS) {
        clearPollingInterval();
        setIsPolling(false);
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [sessionId, clearPollingInterval]);

  const startPolling = useCallback(() => {
    if (intervalRef.current || !sessionId || stoppedByTerminalRef.current) return;
    setIsPolling(true);
    attemptCountRef.current = 0;
    intervalRef.current = setInterval(() => {
      void fetchOnce();
    }, STAKE_POLLING_INTERVAL_MS);
  }, [sessionId, fetchOnce]);

  const stopPolling = useCallback((opts?: { terminal?: boolean }) => {
    clearPollingInterval();
    setIsPolling(false);
    if (opts?.terminal) {
      stoppedByTerminalRef.current = true;
    }
  }, [clearPollingInterval]);

  // Pause/resume on tab visibility
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        clearPollingInterval();
      } else if (isPolling && !stoppedByTerminalRef.current) {
        // Resume: immediate fetch + restart interval
        void fetchOnce();
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            void fetchOnce();
          }, STAKE_POLLING_INTERVAL_MS);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isPolling, fetchOnce, clearPollingInterval]);

  // Reset state and stop stale interval when sessionId changes
  useEffect(() => {
    stoppedByTerminalRef.current = false;
    attemptCountRef.current = 0;
    // Clear any running interval tied to the previous sessionId's fetchOnce closure
    clearPollingInterval();
    setIsPolling(false);
  }, [sessionId, clearPollingInterval]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  return { isPolling, startPolling, stopPolling, fetchNow: fetchOnce };
}
